<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('appointment_details') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeResetDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        template(v-else-if="appointment")
          div.d-flex.align-center
            div.text-sm.font-semibold.neutral500--text {{ $t('patient_detail') }}
            v-spacer
            v-btn.button-xs.neutral700--text.px-0.underline.mr-n2(@click="openEditPatientDetailsDialog = true" small text) {{ $t('edit') }}
          v-card.pa-4.round-lg.mt-1(outlined)
            v-row(no-gutters)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('nric_passport') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.ic || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('email') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.email || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.phone ?  `+${appointment.patient.phone}` : '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('address') }}
                div.text-sm.neutral700--text.mt-1 {{ checkEmptyAddress(appointment.patient.address) ? '-' : getAddressInString(appointment.patient.address) }}

          div.d-flex.align-center.mt-6
            div.text-sm.font-semibold.neutral500--text {{ $t('appointment_detail') }}
            v-spacer
            v-btn.button-xs.neutral700--text.px-0.underline.mr-n2(@click="openEditAppointmentDetailsDialog = true" small text) {{ $t('edit') }}
          v-card.round-lg.mt-1(outlined)
            v-row.pa-4(no-gutters)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('doctor') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.provider.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('service') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.service.name || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('date') }}
                div.text-sm.neutral700--text.mt-1 {{ $options.filters.humanizeDate(appointment.date) || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('time') }}
                div.text-sm.neutral700--text.mt-1 {{ $options.filters.displayTime2(appointment.time) || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_fees') }}
                div.text-sm.neutral700--text.mt-1 {{ (appointment.booking_fee && appointment.booking_fee > 0) ? this.$options.filters.displayPrice(appointment.booking_fee, $currency) : $t('no_fees_required') }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.name || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.phone ?  `+${appointment.lead.phone}` : '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_email') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.email || '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('booking_notes') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.remarks || '-' }}
            template(v-if="appointment.lead.extra_fields_data && appointment.lead.extra_fields_data.length > 0")
              v-divider
              v-row.px-4.pb-4(no-gutters)
                template(v-for="(field, fidx) in appointment.lead.extra_fields_data")
                  v-col.pt-4(v-if="field.enabled" cols="6" :key="`field-${fidx}`")
                    div.text-xs.neutral500--text {{ field.field_name }}
                    div.text-sm.neutral700--text.mt-1 {{ field.data || '-' }}
              v-col.pt-4(v-if="tabIndex === 3 && appointment.lead.order_remarks && appointment.lead.order_remarks.cancelled_reason" cols="6")
                div.text-xs.neutral500--text {{ $t('reason_for_cancellation') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.order_remarks.cancelled_reason }}

          template(v-if="appointment.preliminary_questions && appointment.preliminary_questions.length > 0")
            div.d-flex.align-center.mt-6
              div.text-sm.font-semibold.neutral500--text {{ $t('preliminary_questions') }}
            v-card.px-4.pb-4.round-lg.mt-3(outlined)
              v-row(no-gutters)
                template(v-for="(question, qidx) in appointment.preliminary_questions")
                  v-col.pt-4(v-if="question.enabled" cols="12" :key="`field-${qidx}`")
                    div.text-xs.neutral500--text {{ question.name }}
                    div.text-sm.neutral700--text.mt-1 {{ question.data || '-' }}

          template(v-if="transaction")
            div.d-flex.align-center.mt-6
              div.text-sm.font-semibold.neutral500--text {{ $t('payment_details') }}
            v-card.px-4.pb-4.round-lg.mt-3(outlined)
              v-row(no-gutters)
                v-col.pt-4(cols="12")
                  div.text-xs.neutral500--text {{ $t('payment_date') }}
                  div.text-sm.neutral700--text.mt-1 {{ $options.filters.humanizeDate(transaction.paid_datetime) }}
                v-col.pt-4(cols="12")
                  div.text-xs.neutral500--text {{ $t('payment_method') }}
                  div.text-sm.neutral700--text.mt-1(v-if="transaction.channel") {{ transaction.channel }}
                  div.text-sm.neutral700--text.mt-1(v-else-if="transaction.payment_method") {{ transaction.payment_method }}
                  div.text-sm.neutral700--text.mt-1(v-else) Online

      v-divider
      v-card-actions.py-4(v-if="appointment" :class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(v-if="actionList.find(f => f.id === 'cancel')" @click="openCancelAppointmentDialog = true" text) {{ $t('cancel_appointment') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed outlined @click="clickWhatsapp(appointment)")
          v-icon.ml-n1(left) mdi-whatsapp
          | WhatsApp
        v-menu(offset-y left rounded="lg")
          template(#activator="{ on }")
            v-btn.button-md.ml-3(:loading="isLoading" color="neutral700" depressed dark v-on="on") {{ $t('action') }}
              v-icon.mr-n1(right) mdi-chevron-down
          v-list.py-0
            template(v-for="(action, actidx) in actionList.filter(f => f.id !== 'cancel')")
              template(v-if="action.id !== 'record' || (action.id === 'record' && appointment.paid === false)")
                v-divider(v-if="actidx !== 0")
                v-list-item(@click="action.action" :key="'action-' + actidx")
                  v-list-item-title.text-sm.neutral700--text {{ action.text }}
            template(v-if="appointment.lead.taggings.find(f => f.tagging === 'contacted')")
              v-divider
              v-list-item(@click="openMarkSentPendingDialog = true")
                v-list-item-title.text-sm.neutral700--text {{ $t('mark_sent_pending') }}
    
      .dialogs
        edit-appointment-details(v-if="openEditAppointmentDetailsDialog" :open.sync="openEditAppointmentDetailsDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        edit-patient-details(v-if="openEditPatientDetailsDialog" :open.sync="openEditPatientDetailsDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        mark-as-arrived(v-if="openMarkAsArrivedDialog" :open.sync="openMarkAsArrivedDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        reschedule-date(v-if="openRescheduleDateDialog" :open.sync="openRescheduleDateDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        cancel-appointment(v-if="openCancelAppointmentDialog" :open.sync="openCancelAppointmentDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        follow-up-appointment(v-if="openFollowUpDialog" :open.sync="openFollowUpDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        record-payment(v-if="openRecordPaymentDialog" :open.sync="openRecordPaymentDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        mark-sent-pending(v-if="openMarkSentPendingDialog" :open.sync="openMarkSentPendingDialog" :selectedAppointment="selectedAppointment" @reset="resetData")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import EditAppointmentDetails from '@/components/clinic/dialogs/EditAppointmentDetails.vue'
import EditPatientDetails from '@/components/clinic/dialogs/EditPatientDetails.vue'
import MarkAsArrived from '@/components/clinic/dialogs/MarkAsArrived.vue'
import RescheduleDate from '@/components/clinic/dialogs/RescheduleDate.vue'
import CancelAppointment from '@/components/clinic/dialogs/CancelAppointment.vue'
import FollowUpAppointment from '@/components/clinic/dialogs/FollowUpAppointment.vue'
import RecordPayment from '@/components/clinic/dialogs/RecordPayment.vue'
import MarkSentPending from '@/components/clinic/dialogs/MarkSentPending.vue'

export default {
  name: 'ViewAppointmentDetails',
  components: { EditAppointmentDetails, EditPatientDetails, MarkAsArrived, RescheduleDate, CancelAppointment, FollowUpAppointment, RecordPayment, MarkSentPending },
  props: ['open', 'selectedAppointment', 'tabIndex'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      appointment: null,
      transaction: null,
      openEditAppointmentDetailsDialog: false,
      openEditPatientDetailsDialog: false,
      openMarkAsArrivedDialog: false,
      openRescheduleDateDialog: false,
      openCancelAppointmentDialog: false,
      openFollowUpDialog: false,
      openRecordPaymentDialog: false,
      openMarkSentPendingDialog: false,
      hasChanges: false,
      statuses: [
        { status: 'new,processed', id: 0 },
        { status: 'received', id: 1 },
        { status: 'absent', id: 2 },
        { status: 'cancelled', id: 3 },
      ],
      actions: [
        { id: 'mark', text: this.$t('mark_as_arrived'), action: () => (this.openMarkAsArrivedDialog = true) },
        { id: 'reschedule', text: this.$t('reschedule_new_date'), action: () => (this.openRescheduleDateDialog = true) },
        { id: 'cancel', text: this.$t('cancel_this_appointment'), action: () => (this.openCancelAppointmentDialog = true) },
        { id: 'followup', text: this.$t('create_followup_appointment'), action: () => (this.openFollowUpDialog = true) },
        { id: 'record', text: this.$t('record_payment'), action: () => (this.openRecordPaymentDialog = true) },
      ],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    actionList () {
      const upcoming = ['mark', 'reschedule', 'cancel', 'followup', 'record']
      const completed = ['followup', 'record']
      const absent = ['mark', 'reschedule', 'cancel', 'followup', 'record']
      const cancelled = ['reschedule', 'followup', 'record']
      if (this.tabIndex === 0) { return upcoming.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      if (this.tabIndex === 1) { return completed.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      if (this.tabIndex === 2) { return absent.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      if (this.tabIndex === 3) { return cancelled.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      return this.actions
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          // call transaction data
            if(this.appointment.paid){
              this.$axios.get(`/transactions?lead=${this.appointment.lead.id}&company=${this.company.id}&paid=true`)
                .then(resp => {
                  if (resp.data.count > 0) {
                    this.transaction = resp.data.results[0]
                  }
                })
                .catch(() => {
                  this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
                })
                .finally(() => {
                  this.isFetching = false
                })
           }

        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    confirmAction () {
      this.isLoading = true
      const data = {
        amount: this.appointment.booking_fee,
        gateway: 'direct',
        lead: this.appointment.lead,
        paid: true,
        payment_method: this.payment.payment_method,
        internal_description: this.payment.internal_description
      }
      this.$axios.post('/transactions', data)
        .then(() => {
          this.closeResetDialog()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeResetDialog () {
      this.showDialog = false
      this.hasChanges ? this.$emit('reset') : this.$emit('closed')
    },
    resetData () {
      this.hasChanges = true
      this.fetchAppointment()
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    clickWhatsapp (item) {
      const endpoint = `/leads/${item.lead.pid}/taggings/contacted`
      this.$axios.patch(endpoint)
        .then(resp => {
          this.updateActivityLog(endpoint, resp, item.lead.pid)
          this.hasChanges = true
          const taggings = [resp.data]
          this.$set(item.lead, 'taggings', taggings)
        })
        .catch(() => {})

      let storeUrl = `https://${this.company.slug}.yezza.co/appointment`

      let message = `Hi ${item.patient.name}!%0d%0a%0d%0aWe see that you missed your appointment with ${item.provider.name} at ${this.$options.filters.displayTime2(item.time)}.%0d%0a%0d%0aWould you like to book another appointment? You can do so here:%0d%0a%0d%0a${storeUrl}`
      message = this.$options.filters.encodeSymbols(message)
      if (this.getMessageTemplate()) {
        message = this.getMessageTemplate()
        console.log(message)
        message = message.split('{{name}}').join(item.patient.name ? encodeURIComponent(item.patient.name) : '')
        message = message.split('{{doctor}}').join(item.provider.name ? encodeURIComponent(item.provider.name) : '')
        message = message.split('{{service}}').join(item.service.name ? encodeURIComponent(item.service.name) : '')
        message = message.split('{{date}}').join(item.date ? encodeURIComponent(this.$options.filters.humanizeDate(item.date)) : '')
        message = message.split('{{time}}').join(item.time ? encodeURIComponent(this.$options.filters.displayTime2(item.time)) : '')
        message = message.split('{{appointment_link}}').join(storeUrl || '')
        message = message.split('{{pre_appt_instruction}}').join(item.service.instructions.pre_appointment ? encodeURIComponent(item.service.instructions.pre_appointment.label + '\n\n' + item.service.instructions.pre_appointment.instructions) : '')
        message = message.split('{{post_appt_instruction}}').join(item.service.instructions.post_appointment ? encodeURIComponent(item.service.instructions.post_appointment.label + '\n\n' + item.service.instructions.post_appointment.instructions) : '')
        message = message.split(/\n/g).join('%0d%0a')
        message = this.$options.filters.encodeSymbols(message)
      }

      window.open(`https://api.whatsapp.com/send?phone=${item.patient.phone}&text=${message}`)
    },
    getMessageTemplate () {
      if (!this.clinic) {
        return null
      }
      if (this.tabIndex === 0) {
        return this.clinic.upcoming_reminder
      } else if (this.tabIndex === 2) {
        return this.clinic.absent_reminder
      } else {
        return this.clinic.review_reminder
      }
    },
    updateActivityLog (endpoint, response, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: this.appointmentTab === 0 ? 'Send reminder to upcoming appointment' : this.appointmentTab === 2 ? 'Send reminder to no show appointment' : 'Send reminder to review appointment' ,
        raw_data: { endpoint, response: response.data }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>